import React from "react";
import PropTypes from "prop-types";
import moment from "moment";
import { READINESS_COLORS } from "../../../../constants";
import isEqual from "lodash/isEqual";
import { generateChartArray } from "../../../../utils/readiness-helper";
import ChartTooltip from "../readiness/ChartTooltip";
import { parseTime } from "../sleep-time/SleepTime";

const dateFormat = {
  normal: "YYYY-MM-DD",
  calendar: "MMM D, YYYY",
};

class BarChartComponent extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      tooltip: {
        show: false,
        y: 0,
        x: 0,
        content: "",
      },
    };
    this.chartObject = null;
  }
  // leave this in case of needing it in the future
  // interpolateColors(color1, color2, steps, stepNumber) {
  //   // Parse color strings to get RGB values
  //   const hexToRGB = (hex) => {
  //     hex = hex.replace(/^#/, "");
  //     const bigint = parseInt(hex, 16);
  //     const r = (bigint >> 16) & 255;
  //     const g = (bigint >> 8) & 255;
  //     const b = bigint & 255;
  //     return [r, g, b];
  //   };

  //   const [r1, g1, b1] = hexToRGB(color1);
  //   const [r2, g2, b2] = hexToRGB(color2);

  //   // Calculate the step size for each channel
  //   const stepR = (r2 - r1) / steps;
  //   const stepG = (g2 - g1) / steps;
  //   const stepB = (b2 - b1) / steps;

  //   if (stepNumber >= 0 && stepNumber <= steps) {
  //     const r = Math.round(r1 + stepR * stepNumber);
  //     const g = Math.round(g1 + stepG * stepNumber);
  //     const b = Math.round(b1 + stepB * stepNumber);
  //     const hexColor = `#${((1 << 24) | (r << 16) | (g << 8) | b)
  //       .toString(16)
  //       .slice(1)}`;
  //     return hexColor;
  //   } else {
  //     return null; // Handle an invalid stepNumber
  //   }
  // }

  filterForChart(arr) {
    const { filters } = this.props;
    return arr.map((obj) => {
      const [sore, stress, energy, mood, sleep] = obj.value;
      const updatedReadiness = [
        filters.sore ? sore : 0,
        filters.stress ? stress : 0,
        filters.energy ? energy : 0,
        filters.mood ? mood : 0,
        filters.sleep ? sleep : 0,
      ];
      return { meta: obj.meta, value: updatedReadiness };
    });
  }

  updateChart(newTicks = false) {
    const chartData = generateChartArray(
      this.filterForChart(
        this.props.stats.map((stat) => {
          return { meta: stat.date, value: stat.readiness };
        })
      )
    );

    if (newTicks)
      this.chartObject.update(
        {
          series: chartData || [],
        },
        {
          ticks: newTicks,
          axisY: {
            low: 0,
            high: newTicks[newTicks.length - 1],
          },
        },
        true
      );
    else
      this.chartObject.update({
        series: chartData || [],
      });
  }

  componentDidUpdate(prevProps) {
    const { series, autoScale = false, stats, filters } = this.props;
    const activeFilters =
      filters &&
      Object.keys(filters).reduce((acc, value, idx) => {
        if (filters[value]) {
          return acc + 1;
        } else return acc;
      }, 0);

    const newTicks = [5, 10, 15, 20, 25].slice(0, activeFilters);

    if (
      !isEqual(prevProps.stats, stats) ||
      !isEqual(prevProps.filters, filters)
    ) {
      this.updateChart(newTicks);
    } else if (series) {
      if (!isEqual(prevProps.series, series)) {
        const chartData = {
          series,
        };
        let options = {};

        if (autoScale) {
          const isEmpty = series.length && !series[0].length;
          options = {
            axisY: {
              low: 0,
              high: 100,
            },
          };
          if (!isEmpty) {
            const axisY = this.chartObject.options.axisY;
            delete axisY["low"];
            delete axisY["high"];
            options = {
              axisY,
            };
          }
        }
        this.chartObject.update(chartData, options, true);
      }
    }
  }

  setTooltip(props) {
    const { y = 0, x = 0, show = false } = props;
    this.setState({
      tooltip: {
        show,
        x,
        y,
      },
    });
  }

  fillTooltip(target, valueName) {
    const {
      series,
      colors,
      updateHoveredStat,
      tooltipOffset = 0,
      tooltipUnit = "",
      formatTooltip = false,
      chartName,
    } = this.props;
    const date = target.getAttribute("ct:meta");
    if (!date) return;
    const colorsCopy = [...colors];
    // assumes valuable line data is on the last serie
    const lastSerieIndex = series.length - 1;
    const value = series[lastSerieIndex].find((elem) =>
      moment(elem.meta, dateFormat.calendar).isSame(
        moment(date, dateFormat.calendar)
      )
    ).value;
    const colorIndex =
      colorsCopy.length - 1 - (Math.floor(value - tooltipOffset) - 5);

    const style = `style="color: ${colorsCopy[colorIndex]};"`;

    const isSleep = chartName === "Sleep";
    const displayValue = formatTooltip
      ? Math.round(value - tooltipOffset).toLocaleString()
      : isSleep
      ? parseTime(value)
      : value - tooltipOffset;

    document.querySelector("[role=tooltip]").innerHTML = `
              <div role="tooltip-date">${date}</div>
              <div role="tooltip-content">
                <div role="tooltip-row" >
                ${
                  tooltipUnit || isSleep
                    ? `<span></span>`
                    : `<span>${valueName}</span>&nbsp;`
                }
                  <span ${style}>
                  ${displayValue}${tooltipUnit}
                  </span>
                </div>
              </div>
            `;
    this.setTooltip({
      show: true,
      x: target.getAttribute("x1") - 55,
      y: target.getAttribute("y1") - 70,
    });
    if (updateHoveredStat) updateHoveredStat(date);
  }

  componentDidMount() {
    const {
      series,
      labels,
      ticks = [5, 10, 15, 20, 25],
      chartName,
      colors,
      updateHoveredStat,
      chartOptions = {},
      areaOpacity = 1,
      autoScale = false,
      bodyMeasurement = false,
    } = this.props;

    const Chartist = require("chartist");
    // it changes the render order of the grid and the chart
    (function (window, document, Chartist) {
      "use strict";

      Chartist.plugins = Chartist.plugins || {};
      Chartist.plugins.cgGridOnTop = function () {
        return function cgGridOnTop(chart) {
          if (chart instanceof Chartist.Line || Chartist.Bar) {
            chart.on("created", function () {
              var layerGrid = chart.svg._node.children[0];
              var lastChild = chart.svg._node.children[1];

              lastChild.parentNode.insertBefore(
                layerGrid,
                lastChild.nextSibling
              );
            });
          }
        };
      };
    })(window, document, Chartist);

    const chart = series
      ? series
      : generateChartArray(
          this.props.stats.map((stat) => {
            return { meta: stat.date, value: stat.readiness };
          }) || []
        );
    const data = {
      series: chart,
    };
    const options = Object.assign(
      {
        height: 330,
        ticks: autoScale ? [] : ticks,
        showArea: true,
        showLine: true,
        showPoint: true,
        fullWidth: true,
        lineSmooth: false,
        chartPadding: { left: -10, bottom: -20 },
        axisX: {
          showGrid: false,
          offset: 0,
          showLabel: false,
        },
        axisY: {
          labelOffset: {
            y: 5,
            x: 5,
          },
          onlyInteger: true,
        },
        distributedSeries: true,
        //   plugins: [Chartist.plugins.cgGridOnTop()],
      },
      {
        axisY: autoScale
          ? {}
          : {
              low: 0,
              high: 25,
              onlyInteger: true,
            },
        series: { fillColor: "red" },
      },
      chartOptions
    );
    this.chartObject = new Chartist.Bar(".graph", data, options).on(
      "draw",
      (data) => {
        if (data.type === "area") {
          if (colors) {
            if (data.seriesIndex >= 0 && data.seriesIndex < 6) {
              if (
                (chartName === "KG" || chartName === "LB") &&
                data.seriesIndex === 0
              ) {
                bodyMeasurement
                  ? data.element.attr({
                      style: "fill: url(#body-gradient)",
                    })
                  : data.element.attr({
                      style: `fill: rgba(91, 197, 0, ${areaOpacity})`,
                    });
              } else {
                bodyMeasurement
                  ? data.element.attr({
                      style: "fill: url(#body-gradient)",
                    })
                  : data.element.attr({
                      style: `fill: ${
                        colors[data.seriesIndex]
                      }; fill-opacity: ${areaOpacity};`,
                    });
              }
            }
            if (data.seriesIndex === 6) {
              data.element.attr({
                style:
                  "stroke: #FFFFFF; stroke-width: 0px; fill: rgba(0, 0, 0, .3)",
              });
            }
            if (data.seriesIndex === 7) {
              data.element.attr({
                style:
                  "stroke: #FFFFFF; stroke-width: 0px; fill: rgba(255, 255, 255, .3)",
              });
            }
          } else {
            if (data.seriesIndex === 0) {
              data.element.attr({
                style: `fill: url(#sleep-gradient);`,
              });
            }
            if (data.seriesIndex === 1) {
              data.element.attr({
                style: `fill: url(#mood-gradient);`,
              });
            }
            if (data.seriesIndex === 2) {
              data.element.attr({
                style: `fill: url(#energy-gradient);`,
              });
            }
            if (data.seriesIndex === 3) {
              data.element.attr({
                style: `fill: url(#stress-gradient);`,
              });
            }
            if (data.seriesIndex === 4) {
              data.element.attr({
                style: `fill: url(#sore-gradient);`,
              });
            }
            // average line custom styling
            if (data.seriesIndex === 5) {
              data.element.removeClass("ct-area");
              data.element.addClass("ct-line");
              data.element.attr({
                style: `stroke-width: 0px; stroke: #fff;`,
              });

              // update the path to be drawn in order to display the horizontal line
              const d = data.element._node.getAttribute("d").split(",");
              d[1] = d[2];
              d[1].split("L")[0];
              d[d.length - 1] = d[1].split("L")[0];
              d.toString();
              data.element._node.setAttribute("d", d);
            }
          }
        }
        if (data.type === "line" && !bodyMeasurement) {
          if (data.seriesIndex === 0) {
            data.element.attr({
              style: `stroke: ${READINESS_COLORS.sleep}; stroke-width: ${
                this.props.filters.sleep ? "2px" : "0px"
              }`,
            });
          }
          if (data.seriesIndex === 1) {
            data.element.attr({
              style: `stroke: ${READINESS_COLORS.mood}; stroke-width: ${
                this.props.filters.mood ? "2px" : "0px"
              }`,
            });
          }
          if (data.seriesIndex === 2) {
            data.element.attr({
              style: `stroke: ${READINESS_COLORS.energy}; stroke-width: ${
                this.props.filters.energy ? "2px" : "0px"
              }`,
            });
          }
          if (data.seriesIndex === 3) {
            data.element.attr({
              style: `stroke: ${READINESS_COLORS.stress}; stroke-width: ${
                this.props.filters.stress ? "2px" : "0px"
              }`,
            });
          }
          if (data.seriesIndex === 4) {
            data.element.attr({
              style: `stroke: ${READINESS_COLORS.sore}; stroke-width: ${
                this.props.filters.sore ? "2px" : "0px"
              }`,
            });
          }
        }
        if (data.type === "line" && bodyMeasurement) {
          data.element.attr({
            style:
              "stroke:url(#body-gradient-stroke); stroke-width: 2px; stroke-dasharray:0",
          });
        }
        if (data.type === "grid") {
          data.element.attr({
            style: "stroke: var(--white-10); stroke-dasharray: 5px 5px;",
          });
        }
        if (data.type === "label") {
          data.element.attr({
            style: "color: var(--white-38); font-size: 12px; margin-top: 2px;",
          });
        }
        if (data.type === "point") {
          try {
            data.element._node.addEventListener("mouseenter", ({ target }) => {
              this.fillTooltip(
                target,
                bodyMeasurement ? bodyMeasurement : "RPE"
              );
            });
          } catch (error) {}
          data.element._node.addEventListener("mouseleave", () => {
            this.setTooltip({
              show: false,
            });
            if (updateHoveredStat) updateHoveredStat();
          });
        }
        if (data.type === "bar") {
          data.element.attr({
            // style: `stroke:${this.interpolateColors(
            //   "#00afe4",
            //   "#5bc500",
            //   data.series.length,
            //   data.index
            // )}; stroke-width:calc(100% / ${data.series.length + 1})`,
            style: `stroke:#00afe4; stroke-width:calc(100% / ${
              data.series.length + 1
            })`,
          });
          try {
            data.element._node.addEventListener("mouseenter", ({ target }) => {
              if (series) {
                this.fillTooltip(
                  target,
                  bodyMeasurement ? bodyMeasurement : "RPE"
                );
              }
            });
            data.element._node.addEventListener("mouseleave", () => {
              this.setTooltip({
                show: false,
              });
              if (updateHoveredStat) updateHoveredStat();
            });
          } catch (e) {}
        }
      }
    );
  }

  renderLeyend() {
    const LeyendItem = ({ item }) => (
      <div className="leyend-item">
        <div
          className="color"
          style={{ background: READINESS_COLORS[item.toLowerCase()] }}
        />
        <i>{item}</i>
      </div>
    );
    return (
      <div className="leyend-container">
        <LeyendItem item="Sore" />
        <LeyendItem item="Stress" />
        <LeyendItem item="Energy" />
        <LeyendItem item="Mood" />
        <LeyendItem item="Sleep" />
        <div className="leyend-item">
          <div className="avg-bar" />
          <i>Avg</i>
        </div>
      </div>
    );
  }

  renderAvgText(avg) {
    return (
      <hgroup>
        <p className="s1">Average </p>
        <span>{avg}</span>
      </hgroup>
    );
  }

  renderChart() {
    const { dateFrom, dateTo, labels, bodyClass } = this.props;
    const { tooltip } = this.state;

    return (
      <div
        role="graph-body"
        className={`${bodyClass || ""}`}
        style={{ position: "relative" }}
      >
        <div className="graph" />
        <ChartTooltip
          style={
            tooltip.show
              ? {
                  left: tooltip.x,
                  top: tooltip.y,
                }
              : {
                  display: "none",
                }
          }
        />

        <div role="graph-dates">
          <span>{labels ? labels[0] : dateFrom}</span>
          <span>
            {labels
              ? labels[labels.length - 1]
              : dateTo ||
                moment(new Date(), dateFormat.normal).format(
                  dateFormat.calendar
                )}
          </span>
        </div>
      </div>
    );
  }

  render() {
    const chartName = this.props.chartName || "Score";
    const {
      displayLeyend = true,
      headerRight,
      displayAvgText = false,
    } = this.props;
    const leyend = displayLeyend ? this.renderLeyend() : <div></div>;
    const avgText = displayAvgText ? (
      this.renderAvgText(displayAvgText)
    ) : (
      <div></div>
    );
    const { isComposition } = this.props;
    const rendertitle = () => (
      <div role="graph-title">
        <div role="graph-header">
          <h3>{chartName}</h3>
          {headerRight && (
            <hgroup>
              <p className="s1">{headerRight.name} </p>
              {typeof headerRight.value === "string" &&
              headerRight.value.endsWith(" steps") ? (
                <>
                  <span>{headerRight.value.split(" ")[0]}</span>
                  <span>
                    <b>{headerRight.value.split(" ")[1]}</b>
                  </span>
                </>
              ) : (
                <span>{headerRight.value}</span>
              )}
            </hgroup>
          )}
        </div>
        {avgText ? avgText : leyend}
      </div>
    );

    return (
      <section role="graph-wrapper">
        {!isComposition && rendertitle()}
        {this.renderChart()}
      </section>
    );
  }
}

BarChartComponent.propTypes = {
  chartName: PropTypes.string,
  dateFrom: PropTypes.string,
  dateTo: PropTypes.string,
  stats: PropTypes.array,
  filters: PropTypes.object,
  series: PropTypes.array,
  labels: PropTypes.array,
  colors: PropTypes.array,
  chartOptions: PropTypes.object,
  displayLeyend: PropTypes.bool,
  updateHoveredStat: PropTypes.func,
  areaOpacity: PropTypes.bool,
  bodyMeasurement: PropTypes.string,
  tooltipUnit: PropTypes.string,
  autoScale: PropTypes.bool,
  displayAvgText: PropTypes.bool,
};

export default BarChartComponent;
